import { useR2ObjectContent } from '../hooks/useR2Browser'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Download, AlertCircle } from 'lucide-react'

const SQL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET',
  'DELETE', 'CREATE', 'TABLE', 'INDEX', 'DROP', 'ALTER', 'ADD', 'COLUMN',
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'NOT', 'NULL', 'UNIQUE',
  'DEFAULT', 'AS', 'AND', 'OR', 'IN', 'IS', 'JOIN', 'LEFT', 'RIGHT',
  'INNER', 'ON', 'GROUP', 'BY', 'HAVING', 'ORDER', 'LIMIT', 'OFFSET',
  'INTEGER', 'TEXT', 'REAL', 'BLOB', 'BOOLEAN', 'IF', 'EXISTS', 'BEGIN',
  'COMMIT', 'TRANSACTION', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
])

function highlightSql(text: string): string {
  return text
    .split(/(\b)/)
    .map(tok => SQL_KEYWORDS.has(tok.toUpperCase()) ? `<span class="text-blue-600 dark:text-blue-400 font-semibold">${tok}</span>` : tok)
    .join('')
}

function isSqlKey(key: string, contentType: string): boolean {
  return key.endsWith('.sql') || contentType === 'application/sql'
}

function isJsonKey(key: string, contentType: string): boolean {
  return key.endsWith('.json') || contentType === 'application/json'
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}

export interface R2ObjectContentViewerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bucket: string
  objectKey: string | null
}

export function R2ObjectContentViewer({ open, onOpenChange, bucket, objectKey }: R2ObjectContentViewerProps) {
  const { content, loading, error } = useR2ObjectContent(bucket, open ? objectKey : null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm break-all">{objectKey}</DialogTitle>
        </DialogHeader>

        {loading && <Skeleton className="h-32 w-full" />}

        {error && (
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="size-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {content && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              {content.contentType} · {content.size.toLocaleString()} bytes
              {content.truncated && ' · truncated (>1 MiB)'}
            </div>

            {content.truncated || content.attachment ? (
              <div className="rounded border bg-muted/30 p-4 text-sm flex items-center justify-between gap-3">
                <span className="text-muted-foreground">
                  {content.truncated
                    ? 'File too large to preview inline.'
                    : 'Binary file — preview unavailable.'}
                </span>
                <Button asChild variant="outline" size="sm">
                  <a href={content.downloadUrl} download>
                    <Download className="size-3.5" /> Download
                  </a>
                </Button>
              </div>
            ) : objectKey && isJsonKey(objectKey, content.contentType) ? (
              <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap break-all max-h-[60vh] overflow-y-auto">
                {prettyJson(content.body ?? '')}
              </pre>
            ) : objectKey && isSqlKey(objectKey, content.contentType) ? (
              <pre
                className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap break-all max-h-[60vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: highlightSql(content.body ?? '') }}
              />
            ) : (
              <pre className="rounded border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap break-all max-h-[60vh] overflow-y-auto">
                {content.body ?? ''}
              </pre>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
